import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import { useState, useCallback, type ReactNode } from 'react'

/**
 * Figma (node 124:26):
 * Container: bg-transparent, rounded-md (8px), gap-1 (4px), flex items-center justify-center
 * Sizes: Default h-8 (32px) px-2, sm h-7 (28px) px-2, lg h-9 (36px) px-3
 * Text: text-sm font-medium leading-5 text-foreground whitespace-nowrap
 * Icon: 16px (size-4)
 */
const toggleVariants = cva(
  'inline-flex items-center justify-center gap-1 rounded-md text-sm font-medium whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&>svg]:size-4',
  {
    variants: {
      variant: {
        default: 'bg-transparent hover:bg-muted hover:text-muted-foreground data-[state=on]:bg-accent data-[state=on]:text-accent-foreground',
        outline: 'border border-input bg-transparent hover:bg-accent hover:text-accent-foreground data-[state=on]:bg-accent data-[state=on]:text-accent-foreground',
      },
      size: {
        sm: 'h-7 px-2 text-xs',
        default: 'h-8 px-2',
        lg: 'h-9 px-3',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  }
)

export interface IntuiToggleProps extends VariantProps<typeof toggleVariants> {
  children?: ReactNode
  pressed?: boolean
  defaultPressed?: boolean
  onPressedChange?: (pressed: boolean) => void
  disabled?: boolean
  className?: string
  'aria-label'?: string
}

function IntuiToggle({ children, variant, size, pressed, defaultPressed = false, onPressedChange, disabled, className, 'aria-label': ariaLabel }: IntuiToggleProps) {
  const [internalPressed, setInternalPressed] = useState(defaultPressed)
  const isControlled = pressed !== undefined
  const isPressed = isControlled ? pressed : internalPressed

  const handleClick = useCallback(() => {
    const next = !isPressed
    if (!isControlled) setInternalPressed(next)
    onPressedChange?.(next)
  }, [isPressed, isControlled, onPressedChange])

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isPressed}
      aria-label={ariaLabel}
      data-state={isPressed ? 'on' : 'off'}
      disabled={disabled}
      onClick={handleClick}
      className={cn(toggleVariants({ variant, size }), className)}
    >
      {children}
    </button>
  )
}

export { IntuiToggle, toggleVariants }
